// app/components/approval/ApprovalRequiredPanel.tsx
import { useCallback, useState, useEffect, useRef } from "react";
import ApprovalTabs from "./ApprovalTabs";
import ApprovalList from "./ApprovalList";
import ApproveAllFooter from "./ApproveAllFooter";
import type { ApprovalCardItem, ApprovalTabId } from "../../types/approval";
import {
  getApprovalData,
  moduleTypeToTabId,
} from "../../mappers/approvalDataSource";
import { buildApprovalTabs } from "../../mappers/approvalMapper";
import RtpApprovalContainer from "./RtpApprovalContainer";
import TxnApprovalContainer from "./TxnApprovalContainer";
import { t } from "i18next";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import { gxBeneficiarySingleApproveApi } from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import { gxDashboardPendingApprovalCountApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import AppLoader from "@/app/components/webloading/loader/AppLoader";
import BeneSuccessModal from "@/app/models/dialogs/BeneSuccessModal";
import { RemarksModal } from "@/app/models/dialogs/RemarksModal";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import "./styles/ApprovalRequiredPanel.scss";
import { Loader } from "@progress/kendo-react-indicators";

const PAGE_SIZE_API = 10;
const PENDING_APPROVAL_STATUS_CODE = "4415";

interface ApprovalRequiredPanelProps {
  title?: string;
  access?: { view: boolean; approve: boolean };
  callCount: boolean;
  setCallCount: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenBeneList: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenBulkUploadList: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenPayrollsList: React.Dispatch<React.SetStateAction<boolean>>;
}

const ApprovalRequiredPanel = ({
  access = { view: true, approve: true },
  callCount,
  setCallCount,
  setIsOpenBeneList,
  setIsOpenBulkUploadList,
  setIsOpenPayrollsList,
}: ApprovalRequiredPanelProps) => {
  const [activeTabId, setActiveTabId] = useState<ApprovalTabId>("all");
  const [items, setItems] = useState<ApprovalCardItem[]>([]);

  const [tabCounts, setTabCounts] = useState<Record<ApprovalTabId, number>>({
    all: 0,
    rtp: 0,
    bene: 0,
    bulk: 0,
    payrolls: 0,
    txn: 0,
  });

  const [allTabTotal, setAllTabTotal] = useState<number>(0);
  const [countLoading, setCountLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openContainer, setOpenContainer] = useState<ApprovalTabId | null>(
    null,
  );
  const [containerMode, setContainerMode] = useState<"list" | "detail">("list");
  const [detailRawData, setDetailRawData] = useState<unknown>(null);
  const [detailTriggerKey, setDetailTriggerKey] = useState<number>(0);
  const [isCardActionLoading, setIsCardActionLoading] = useState(false);
  const [rejectModalItem, setRejectModalItem] =
    useState<ApprovalCardItem | null>(null);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [rejectRemarksError, setRejectRemarksError] = useState("");
  const [cardSuccessData, setCardSuccessData] = useState<{
    aprdStatus: "A" | "R";
    beneName: string | null;
  } | null>(null);

  // --- Infinite scroll / load-more state ---
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);
  const [pageNo, setPageNo] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // --- Paginated fetch fetchData ---
  const loadTabPage = useCallback(
    async (tabId: ApprovalTabId, page: number) => {
      if (isFetchingRef.current) return;
      if (page === 1) {
        setHasMorePages(true);
      } else if (!hasMorePages) {
        return;
      }

      isFetchingRef.current = true;
      if (page === 1) setIsLoading(true);
      else setIsLoadingMore(true);

      try {
        const data = await getApprovalData(tabId, {
          pageNo: page,
          pageSize: PAGE_SIZE_API,
        });

        if (data.length === 0) {
          setHasMorePages(false);
          if (page === 1) setItems([]);
          return;
        }

        setItems((prev) => (page === 1 ? data : [...prev, ...data]));
        setTabCounts((prev) => ({
          ...prev,
          [tabId]: page === 1 ? data.length : prev[tabId] + data.length,
        }));
        setPageNo(page);

        if (data.length < PAGE_SIZE_API) {
          setHasMorePages(false);
        }
      } catch (error) {
        setHasMorePages(false);
        if (page === 1) setItems([]);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [hasMorePages],
  );

  const fetchPendingApprovalCount = useCallback(async () => {
    setCountLoading(true);
    try {
      const result = await gxDashboardPendingApprovalCountApi({
        status: PENDING_APPROVAL_STATUS_CODE,
      });
      setAllTabTotal(result?.data?.total ?? 0);
    } catch (error) {
      setAllTabTotal(0);
    } finally {
      setCountLoading(false);
    }
  }, []);

  useEffect(() => {
    setItems([]);
    setPageNo(1);
    setHasMorePages(true);
    isFetchingRef.current = false;
    setIsPanelExpanded(false);

    loadTabPage(activeTabId, 1);
  }, [activeTabId]);

  useEffect(() => {
    fetchPendingApprovalCount();
  }, [fetchPendingApprovalCount]);

  useEffect(() => {
    if (callCount) {
      loadTabPage(activeTabId, 1);
      fetchPendingApprovalCount();
    }
  }, [callCount]);

  const handleLoadMore = () => {
    setIsPanelExpanded(true);
    if (hasMorePages && !isFetchingRef.current) {
      loadTabPage(activeTabId, pageNo + 1);
    }
  };

  useEffect(() => {
    if (!isPanelExpanded || !panelRef.current) return;

    const el = panelRef.current;

    const handleScroll = () => {
      if (
        isFetchingRef.current ||
        !hasMorePages ||
        isLoadingMore ||
        isLoading
      ) {
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = el;
      const reachedBottom = scrollTop + clientHeight >= scrollHeight - 24;

      if (reachedBottom) {
        loadTabPage(activeTabId, pageNo + 1);
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [
    isPanelExpanded,
    hasMorePages,
    isLoadingMore,
    isLoading,
    activeTabId,
    pageNo,
    loadTabPage,
  ]);

  const tabs = buildApprovalTabs({ ...tabCounts, all: allTabTotal });

  const approveAllCount = items.filter(
    (item) =>
      item.numberOfApproved < item.numberOfLevels &&
      item.paymentStatusCode !== 4447,
  ).length;

  const handleTabChange = (tabId: ApprovalTabId) => {
    setActiveTabId(tabId);
  };

  const handleCardClick = (item: ApprovalCardItem) => {
    const resolvedTabId: Exclude<ApprovalTabId, "all"> | null =
      item.tabId === "all"
        ? moduleTypeToTabId((item.raw as any)?.moduleType)
        : item.tabId;

    const normalizedRaw =
      resolvedTabId === "bene"
        ? {
            ...(item.raw as any),
            beneId: (item.raw as any)?.beneId ?? item.transId,
          }
        : item.raw;

    setDetailRawData(normalizedRaw);
    setDetailTriggerKey((prev) => prev + 1);
    setContainerMode("detail");

    switch (resolvedTabId) {
      case "bene":
        setIsOpenBeneList(true);
        break;
      case "bulk":
        setIsOpenBulkUploadList(true);
        break;
      case "payrolls":
        setIsOpenPayrollsList(true);
        break;
      case "rtp":
      case "txn":
        setOpenContainer(resolvedTabId);
        break;
      default:
    }
  };

  const handleCardApprove = async (transId: number) => {
    const item = items.find((i) => i.transId === transId);
    if (!item) return;

    if (item.tabId === "bene") {
      setIsCardActionLoading(true);
      try {
        const beneId = (item.raw as any)?.beneId;
        const payload: any = { beneId, aprdStatus: "A" };
        const result = await gxBeneficiarySingleApproveApi(payload);
        if (result?.status === 1) {
          setCardSuccessData({
            aprdStatus: "A",
            beneName: (item.raw as any)?.beneName ?? null,
          });
          setCallCount(true);
          await loadTabPage(activeTabId, 1);
          await fetchPendingApprovalCount();
        }
      } finally {
        setIsCardActionLoading(false);
      }
      return;
    }

    handleCardClick(item);
  };

  const handleCardReject = (transId: number) => {
    const item = items.find((i) => i.transId === transId);
    if (!item) return;

    if (item.tabId === "bene") {
      setRejectModalItem(item);
      return;
    }

    handleCardClick(item);
  };

  const submitCardReject = async () => {
    if (!rejectRemarks.trim()) {
      setRejectRemarksError("Reject reason is required");
      return;
    }
    if (!rejectModalItem) return;

    setIsCardActionLoading(true);
    try {
      const beneId = (rejectModalItem.raw as any)?.beneId;
      const payload: any = {
        beneId,
        aprdStatus: "R",
        remarks: rejectRemarks,
      };
      const result = await gxBeneficiarySingleApproveApi(payload);
      if (result?.status === 1) {
        setCardSuccessData({
          aprdStatus: "R",
          beneName: (rejectModalItem.raw as any)?.beneName ?? null,
        });
        setCallCount(true);
        await loadTabPage(activeTabId, 1);
        await fetchPendingApprovalCount();
      }
    } finally {
      setIsCardActionLoading(false);
      setRejectModalItem(null);
      setRejectRemarks("");
      setRejectRemarksError("");
    }
  };

  const handleApproveAll = () => {
    if (activeTabId === "all") return;
    setOpenContainer(activeTabId);
    setContainerMode("list");
  };

  const closeContainer = () => {
    setOpenContainer(null);
    setContainerMode("list");
    setDetailRawData(null);
  };

  return (
    <div
      ref={panelRef}
      className={`gx-approval-panel relative ${
        isPanelExpanded ? "gx-approval-panel--scrollable" : ""
      }`}
    >
      {countLoading && (
        <div className="gx-bene-form-container-loading-overlay">
          <SubmitLoading />
        </div>
      )}

      <div className="gx-approval-panel__header">
        <h2 className="gx-approval-panel__title gx-p18-b">
          {t("approval_required")}
        </h2>
        <ApprovalTabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabChange={handleTabChange}
        />
      </div>

      {isLoading && (
        <div className="gx-converging-spinner">
          <Loader size="large" type="converging-spinner" />
        </div>
      )}

      {!isLoading && items.length === 0 ? (
        <div className="gx-approval-panel__empty">
          <NoDataFound message="No approvals found" />
        </div>
      ) : (
        <ApprovalList
          items={items}
          onCardClick={handleCardClick}
          onReject={handleCardReject}
          onApprove={handleCardApprove}
        />
      )}

      {isLoadingMore && (
        <div className="gx-approval-panel__loading-more">
          <SubmitLoading />
        </div>
      )}

      {isCardActionLoading && <AppLoader />}

      {items.length > 0 && (
        <ApproveAllFooter
          hasMore={hasMorePages}
          approveAllCount={activeTabId === "all" ? 0 : approveAllCount}
          onLoadMore={handleLoadMore}
          onApproveAll={handleApproveAll}
        />
      )}

      {openContainer === "rtp" && (
        <RtpApprovalContainer
          isOpenRtpList={containerMode === "list"}
          setIsOpenRtpList={(open) => !open && closeContainer()}
          initialTransaction={
            containerMode === "detail" ? (detailRawData as any) : null
          }
          initialTransactionKey={detailTriggerKey}
          access={access}
          setCallCount={setCallCount}
        />
      )}

      {openContainer === "txn" && (
        <TxnApprovalContainer
          isOpenTxnList={containerMode === "list"}
          setIsOpenTxnList={(open) => !open && closeContainer()}
          initialTransaction={
            containerMode === "detail" ? (detailRawData as any) : null
          }
          initialTransactionKey={detailTriggerKey}
          access={access}
          setCallCount={setCallCount}
        />
      )}

      {rejectModalItem && (
        <RemarksModal
          isOpen={!!rejectModalItem}
          onClose={() => {
            setRejectModalItem(null);
            setRejectRemarks("");
            setRejectRemarksError("");
          }}
          errors={{ note: { message: rejectRemarksError } }}
          setValue={() => {}}
          isReject
          value={rejectRemarks}
          onChange={(value) => {
            setRejectRemarks(value);
            if (value.trim()) setRejectRemarksError("");
          }}
          submit={submitCardReject}
        />
      )}

      {cardSuccessData && (
        <BeneSuccessModal
          isOpen={!!cardSuccessData}
          onClose={() => {
            setCardSuccessData(null);
            setCallCount(false);
          }}
          successData={cardSuccessData}
        />
      )}
    </div>
  );
};

export default ApprovalRequiredPanel;
